import React, { useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { KeyboardContext } from "../../utilities/virtual-keyboard";
import { SET_VIRTUAL_KEYBOARD } from "../../store/actions/sessionActions";
import { getTouchScreen } from "../../store/sagas/selectors";

interface InputProps {
    error?: boolean;
}

interface LabelProps {
    inputLabel?: string;
}

const FormInputContainer = styled.div<InputProps>`
    background-color: #fff;
    border-radius: 4px;
    transition: box-shadow 0.2s ease-in-out;
    padding: 10px 6px;
    width: calc(100% - 14px);
    border: thin solid ${props => (props.error ? "red" : "#d0d0d0")};
    margin-bottom: 10px;
    &:hover {
        box-shadow: 0px 0px 0px 1px #ccc inset;
    }
    &:focus-within {
        box-shadow: 0px 0px 0px 1px #ccc inset;
    }
`;

const FormInputLabel = styled.label<LabelProps>`
    display: block;
    position: relative;
    max-height: 0;
    font-weight: 500;
    pointer-events: none;
    &::before {
        content: "${props => props.inputLabel}";
        display: inline-block;
        backface-visibility: hidden;
        transform-origin: left top;
        transition: transform 0.2s ease;
        position: absolute;
        background: #fff;
        padding: 0px 4px 0px 4px;
        left: 0px;
        transform: translate3d(0, -2.4rem, 0) scale3d(0.82, 0.82, 1);
    }
    &::after {
        bottom: 1rem;
        content: "";
        height: 0.1rem;
        position: absolute;
        transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms cubic-bezier(0.4, 0, 0.2, 1),
            background-color 0.3s ease;
        opacity: 0;
        left: 0;
        top: 100%;
        margin-top: -0.1rem;
        transform: scale3d(0, 1, 1);
        width: 100%;
        box-sizing: inherit;
    }
    span {
        display: none;
    }
`;

const FormInput = styled.input`
    ${({ theme }) => theme.bp.xs`
        font-size: 14px;
    `};
    ${({ theme }) => theme.bp.xl`
        font-size: 14px;
    `};
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}

    width: 100%;
    padding: 1.8rem 1 rem 0.6rem;
    outline: none;
    background: none;
    border-width: 0;
    &::placeholder {
        opacity: 0;
    }

    &:focus + ${FormInputLabel}::after {
        transform: scale3d(1, 1, 1);
        opacity: 1;
    }

    &:placeholder-shown + ${FormInputLabel}::before {
        transform: translate3d(0, -1.2rem, 0) scale3d(1, 1, 1);
    }

    &:focus + ${FormInputLabel}::before {
        transform: translate3d(0, -2.4rem, 0) scale3d(0.82, 0.82, 1);
    }
`;

const InputFloatLabel = ({
    name,
    error,
    setEvent,
    autoComplete,
    required,
}: {
    name: string;
    error?: boolean;
    setEvent: Function;
    autoComplete?: string;
    required?: boolean;
}) => {
    const dispatch = useDispatch();
    const VirtualKeyboard = useContext(KeyboardContext);
    const touchScreen = useSelector(getTouchScreen);
    const [formValue, setValue] = useState("");

    const changeValue = (v: string) => {
        setValue(v);
        setEvent(v);
    };

    const onInputFocus = (value: boolean, currentValue: string) => {
        if (!touchScreen) return;

        if (value) {
            VirtualKeyboard.show(input => changeValue(input), currentValue);
        } else {
            VirtualKeyboard.hide();
        }

        dispatch({ type: SET_VIRTUAL_KEYBOARD, showVirtualKeyboard: value });
    };

    return (
        <FormInputContainer error={error}>
            <FormInput
                key={name}
                placeholder={required ? `*${name}` : name}
                value={formValue}
                autoComplete={autoComplete || "on"}
                onFocus={event => onInputFocus(true, event.target.value)}
                onBlur={event => onInputFocus(false, event.target.value)}
                onChange={event => changeValue(event.target.value)}
                required={required}
            />
            <FormInputLabel inputLabel={required ? `*${name}` : name}>
                <span>{required ? `*${name}` : name}</span>
            </FormInputLabel>
        </FormInputContainer>
    );
};

export default InputFloatLabel;
